import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const {
  FiArrowLeft, FiArrowRight, FiUploadCloud, FiList, FiGlobe, FiEdit3, FiShield, FiHeart, FiBookOpen, FiCheckCircle, FiAlertTriangle,
} = FiIcons;

/* per-step visuals */
const IngestViz = () => (
  <div className="bg-white rounded-2xl border-2 border-dashed border-primary-200 p-8 text-center">
    <SafeIcon icon={FiUploadCloud} className="w-10 h-10 text-primary-500 mx-auto mb-3" />
    <div className="text-sm text-gray-500 mb-3">Drop your manuscript</div>
    <div className="flex justify-center gap-2">{['DOCX', 'EPUB', 'TXT', 'PDF'].map((f) => <span key={f} className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 text-gray-600">{f}</span>)}</div>
  </div>
);
const ChaptersViz = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    <div className="text-[10px] font-bold uppercase text-gray-400 mb-3">Chapters detected · 22</div>
    {['1 · The Internat', '2 · Bread & Snow', '3 · The Name They Gave Me', '4 · Leaving'].map((c, i) => (
      <div key={c} className="flex items-center gap-2 text-sm text-gray-700 py-1.5 border-b border-gray-50 last:border-0">
        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500" /> {c}
      </div>
    ))}
    <div className="text-[11px] text-gray-400 mt-2">+ 18 more · auto-detected from your file</div>
  </div>
);
const CultureViz = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    <div className="grid grid-cols-3 gap-2 mb-3">
      {[['Culture', 'Ukrainian'], ['Language', 'Ukrainian'], ['Script', 'Cyrillic']].map(([k, v]) => (
        <div key={k} className="bg-gray-50 rounded-lg px-2 py-1.5"><div className="text-[9px] uppercase text-gray-400">{k}</div><div className="text-xs font-bold text-gray-800">{v}</div></div>
      ))}
    </div>
    <div className="flex flex-wrap gap-1.5">{['Orphanhood', 'Soviet context', 'Orthodox heritage', 'Identity'].map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary-50 text-secondary-700">{t}</span>)}</div>
  </div>
);
const VoiceViz = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    {[['Sentence rhythm', 70], ['Tone — reflective', 82], ['Imagery', 64], ['Dialogue', 38]].map(([l, v]) => (
      <div key={l} className="mb-2"><div className="flex justify-between text-[11px] text-gray-500 mb-1"><span>{l}</span><span>{v}</span></div>
        <div className="w-full bg-gray-100 rounded-full h-2"><div className="h-2 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500" style={{ width: `${v}%` }} /></div></div>
    ))}
  </div>
);
const AuthViz = () => (
  <div className="grid grid-cols-3 gap-3">
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-center"><div className="text-2xl font-bold text-emerald-600">96%</div><div className="text-[10px] text-gray-600">human</div></div>
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-center"><div className="text-2xl font-bold text-gray-700">4%</div><div className="text-[10px] text-gray-600">AI-likely</div></div>
    <div className="rounded-xl border border-primary-200 bg-primary-50/60 p-3 text-center"><div className="text-2xl font-bold text-primary-600">95%</div><div className="text-[10px] text-gray-600">your voice</div></div>
  </div>
);
const ReadersViz = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    <div className="text-[10px] font-bold uppercase text-gray-400 mb-3">Readers who love these will love yours</div>
    <div className="grid grid-cols-2 gap-3">
      {[['Educated', 'Tara Westover'], ['The Glass Castle', 'J. Walls']].map(([t, a]) => (
        <div key={t} className="flex items-center gap-2"><div className="w-9 h-12 rounded bg-gradient-to-br from-sky-500 to-amber-500" /><div><div className="text-xs font-semibold text-gray-800">{t}</div><div className="text-[10px] text-gray-400">{a}</div></div></div>
      ))}
    </div>
  </div>
);

const STEPS = [
  { icon: FiUploadCloud, kicker: 'You hand us the book', title: 'However it exists today', emotional: 'A messy DOCX. A half-finished EPUB. Half a draft. Bring it as it is — we meet your book where it lives.', viz: IngestViz },
  { icon: FiList, kicker: 'We find its shape', title: 'Every chapter, automatically', emotional: 'We read the whole thing — not a sample — and lay out its chapters and scenes, even when your file never told us where they were.', viz: ChaptersViz },
  { icon: FiGlobe, kicker: 'We learn its world', title: 'The culture inside your story', emotional: 'Heritage, language, the scripts your names are written in, the themes underneath. So a Ukrainian orphanage reads like one - never flattened into "generic Eastern Europe."', viz: CultureViz },
  { icon: FiEdit3, kicker: 'We learn you', title: 'The fingerprint of your voice', emotional: 'Your rhythm, your restraint, the way you open small and widen into history. Not to copy a style — to protect yours.', viz: VoiceViz },
  { icon: FiShield, kicker: 'We make sure it\'s really you', title: 'Sounds human. Sounds like you.', emotional: 'We score how likely it reads as AI, how human it sounds, and how closely it matches your voice — so no one can ever say a machine wrote your book.', viz: AuthViz },
  { icon: FiHeart, kicker: 'We find your readers', title: 'The shelf you belong on', emotional: 'The books yours sits beside, and the categories and keywords that put you in front of the people already looking for you.', viz: ReadersViz },
];

const HowWeReadPage = () => {
  const structuredData = {
    '@context': 'https://schema.org', '@type': 'WebPage',
    name: 'How ManuscriptSmith reads your book',
    description: 'The journey from manuscript to understanding: chapter breakdown, cultural analysis, Author Voice DNA, authenticity scoring, and comparable titles.',
    url: 'https://manuscriptsmith.com/how-we-read-your-book',
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="How We Read Your Book — The Story Behind the Analysis | ManuscriptSmith"
        description="Hand us your manuscript and watch what happens: we read every chapter, learn its culture and themes, capture the fingerprint of your voice, check it reads as authentically human, and find the readers who'll love it."
        keywords="how ai reads a book, manuscript analysis process, author voice fingerprint, cultural analysis, chapter breakdown, ai authenticity check"
        canonicalUrl="https://manuscriptsmith.com/how-we-read-your-book"
        structuredData={structuredData}
      />

      <main>
        {/* Hero */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">
            Hand us your book. <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">Here's what happens next.</span>
          </h1>
          <p className="text-xl text-gray-600">
            Most tools wait for you to type a prompt. We start by <em>reading</em> — the way a great editor would, the
            first time they fall for your manuscript. This is what that looks like.
          </p>
        </section>

        {/* Steps */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          {STEPS.map((s, i) => {
            const Viz = s.viz;
            return (
              <div key={s.kicker} className="grid md:grid-cols-2 gap-8 items-center py-10 border-b border-parchment-200 last:border-0">
                <motion.div className={i % 2 ? 'md:order-2' : ''}
                  initial={{ opacity: 0, x: i % 2 ? 24 : -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                  <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary-700 bg-secondary-50 px-3 py-1 rounded-full mb-3">
                    <SafeIcon icon={s.icon} className="w-4 h-4" /> Step {i + 1} · {s.kicker}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{s.title}</h2>
                  <p className="text-lg text-gray-600">{s.emotional}</p>
                </motion.div>
                <motion.div className={i % 2 ? 'md:order-1' : ''}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
                  <Viz />
                </motion.div>
              </div>
            );
          })}
        </section>

        {/* The feeling */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <SafeIcon icon={FiBookOpen} className="w-10 h-10 text-primary-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">And then it hands the book back to you — understood.</h2>
          <p className="text-lg text-gray-600">
            It's a feeling most writers have never had from software: the sense that something actually <em>read</em>
            your book, and got it. From there, every word it helps you write is in your voice, on your terms.
          </p>
          <Link to="/analysis" className="inline-flex items-center mt-6 text-primary-600 font-semibold hover:underline">
            See a full analysis report <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-1" />
          </Link>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">Let us read yours</h2>
            <p className="text-lg opacity-90 mb-8">Import a draft or paste a sample. We'll show you your book, understood.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50">
                Analyze my book <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/editor" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25">
                Try the live editor
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HowWeReadPage;
